import { User } from '@/types/api';
import { create } from 'zustand';

import { isWebGLSupported, isLowEndDevice } from '@/utils/hardwareDetection';

import useMapsStore from './maps';

export type MapEngineMode = 'maps-gl' | 'maps-leaflet';

interface AuthStore {
  user: User | null;
  setLogin: (user: User) => void;
  logout: () => void;
  sidebar: boolean;
  setSidebar: (show: boolean) => void;
  firstAccess: boolean;
  setFirstAccess: (firstAccess: boolean) => void;
  mapMode: MapEngineMode;
  setMapMode: (mode: MapEngineMode | 'leaflet' | 'webgl', reload?: boolean) => void;
  useWebGL: boolean;
  setUseWebGL: (useWebGL: boolean, reload?: boolean) => void;
}

const getInitialMapMode = (): MapEngineMode => {
  if (typeof window === 'undefined') return 'maps-gl';

  // Capability Guard: Fallback ke Leaflet jika browser/device tidak support WebGL
  if (!isWebGLSupported()) {
    return 'maps-leaflet';
  }

  try {
    const saved = localStorage.getItem('map_mode');
    if (saved === 'maps-gl' || saved === 'webgl') {
      return 'maps-gl';
    }
    if (saved === 'maps-leaflet' || saved === 'leaflet') {
      return 'maps-leaflet';
    }
    const prev = localStorage.getItem('prev_map_mode_before_weather');
    if (prev === 'maps-gl') {
      return 'maps-gl';
    }
  } catch (e) {}

  // Default saat belum disetel: cek spesifikasi perangkat (RAM < 4GB atau CPU < 4 cores -> Leaflet)
  return !isLowEndDevice() ? 'maps-gl' : 'maps-leaflet';
};

const initialMapMode = getInitialMapMode();

const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  setLogin: (user) => set({ user: user }),
  logout: () => set({ user: null }),
  sidebar: true,
  setSidebar: (show: boolean) => set({ sidebar: show }),
  firstAccess: true,
  setFirstAccess: (firstAccess: boolean) => set({ firstAccess: firstAccess }),
  mapMode: initialMapMode,
  setMapMode: (mode: MapEngineMode | 'leaflet' | 'webgl', reload = true) => {
    let normalizedMode: MapEngineMode = (mode === 'maps-gl' || mode === 'webgl') ? 'maps-gl' : 'maps-leaflet';

    // Capability Guard: jika memilih webgl tapi browser/hardware tidak mendukung, paksa fallback ke leaflet
    if (normalizedMode === 'maps-gl' && !isWebGLSupported()) {
      normalizedMode = 'maps-leaflet';
    }

    try {
      localStorage.setItem('map_mode', normalizedMode);
    } catch (e) {}
    set({ mapMode: normalizedMode, useWebGL: normalizedMode === 'maps-gl' });
    if (reload && typeof window !== 'undefined') {
      window.location.reload();
    }
  },
  useWebGL: initialMapMode === 'maps-gl',
  setUseWebGL: (useWebGL: boolean, reload = true) => {
    let mode: MapEngineMode = useWebGL ? 'maps-gl' : 'maps-leaflet';
    if (mode === 'maps-gl' && !isWebGLSupported()) {
      mode = 'maps-leaflet';
    }
    try {
      localStorage.setItem('map_mode', mode);
    } catch (e) {}
    set({ mapMode: mode, useWebGL: mode === 'maps-gl' });
    if (reload && typeof window !== 'undefined') {
      window.location.reload();
    }
  },
}));

export default useAuthStore;
